import React, { useEffect, useState } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { ProveedorDatosApi, useDatosApi } from './pasos/1_consultar_datos_api';
import GeneraMenuGrupos from './pasos/3_genera_menu_grupos';
import formsService from './utils/formsService';
import LinkDownloadDashboard from '../../../components/link/link-download-dashboard.jsx';
import FichasSidebar from './components/FichasSidebar.jsx';
import BotonVolverArriba from './components/BotonVolverArriba.jsx';
import { debugLog } from './utils/debugLog';

/**
 * Componente que muestra directamente el menú de grupos procesados
 * sin pasos, consultando la API automáticamente con el UUID proporcionado
 */
const FormMasterDirectoContent = () => {
  const { uuid } = useParams();
  const history = useHistory();
  const { cargarTodosLosDatos, estadoCarga, progresoCarga, errores, datosFormulario } = useDatosApi();
  const [cargaCompleta, setCargaCompleta] = useState(false);
  const [uuidSeleccionado, setUuidSeleccionado] = useState(uuid || null);
  const [isSidebarVisible, setSidebarVisible] = useState(false);
  const [selectedFichaId, setSelectedFichaId] = useState(uuid || null);
  const handleToggleSidebar = () => setSidebarVisible(prev => !prev);
  const handleFichaSelect = (ficha) => {
    const selectedUuid = ficha?.uuid;
    if (selectedUuid) {
      setUuidSeleccionado(selectedUuid);
      setSelectedFichaId(selectedUuid);
      setSidebarVisible(false);
      // Reflejar el uuid en la URL para permitir deep-linking y compartir el enlace
      if (selectedUuid !== uuid) {
        history.push(`/admin/formulario/master/${selectedUuid}`);
      }
    }
  };

  // Si el uuid de la URL cambia (navegación atrás/adelante o link directo), sincronizar selección
  useEffect(() => {
    if (uuid && uuid !== uuidSeleccionado) {
      setUuidSeleccionado(uuid);
      setSelectedFichaId(uuid);
    }
  }, [uuid]); // eslint-disable-line react-hooks/exhaustive-deps

  // Cerrar el sidebar si se selecciona un UUID (evita reapertura por re-render)
  useEffect(() => {
    if (uuidSeleccionado) {
      setSidebarVisible(false);
    }
  }, [uuidSeleccionado]);

  // Cerrar el sidebar cuando el formulario termina de cargar
  useEffect(() => {
    if (cargaCompleta && datosFormulario) {
      setSidebarVisible(false);
    }
  }, [cargaCompleta, datosFormulario]);

  // Obtener información general para mostrar datos en el encabezado
  const infoGeneral = formsService.getInfoGeneral();

  useEffect(() => {
    const cargarDatos = async () => {
      if (uuidSeleccionado) {
        try {
          // Configurar información de debug antes de cargar datos
          formsService.setDebugInfoGeneral({
            cuenta_kobo: 'sirio_flor_viva',
          });

          debugLog('🔄 Cargando datos para UUID:', uuidSeleccionado);
          await cargarTodosLosDatos(uuidSeleccionado);
          debugLog('✅ Datos cargados exitosamente');
          setCargaCompleta(true);
        } catch (error) {
          console.error('❌ Error al cargar datos:', error);
          setCargaCompleta(true); // Permitir que se muestre aunque haya error
        }
      }
    };

    cargarDatos();
  }, [uuidSeleccionado, cargarTodosLosDatos]);

  // Mostrar estado de carga
  if (estadoCarga === 'cargando') {
    return (
      <div
        style={{
          minHeight: '100vh',
          backgroundColor: '#f8f9fa',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
        <div style={{ textAlign: 'center' }}>
          <div
            style={{
              fontSize: '2em',
              marginBottom: '20px',
              color: '#007bff',
            }}>
            🔄 Cargando datos...
          </div>
          <div
            style={{
              width: '300px',
              height: '20px',
              backgroundColor: '#e9ecef',
              borderRadius: '10px',
              overflow: 'hidden',
            }}>
            <div
              style={{
                width: `${progresoCarga}%`,
                height: '100%',
                backgroundColor: '#007bff',
                transition: 'width 0.3s ease',
              }}></div>
          </div>
          <div
            style={{
              marginTop: '10px',
              color: '#666',
            }}>
            {progresoCarga}% completado
          </div>
          <div
            style={{
              marginTop: '15px',
              fontSize: '0.9em',
              color: '#888',
            }}>
            UUID: {uuidSeleccionado}
          </div>
        </div>
      </div>
    );
  }

  // Mostrar errores si los hay
  if (errores && Object.keys(errores).length > 0) {
    return (
      <div
        style={{
          minHeight: '100vh',
          backgroundColor: '#f8f9fa',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
        <div
          style={{
            textAlign: 'center',
            maxWidth: '600px',
            padding: '40px',
            backgroundColor: '#fff',
            borderRadius: '8px',
            boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
          }}>
          <div
            style={{
              fontSize: '2em',
              marginBottom: '20px',
              color: '#dc3545',
            }}>
            ❌ Error al cargar datos
          </div>
          <div
            style={{
              marginBottom: '20px',
              color: '#666',
            }}>
            Se encontraron los siguientes errores:
          </div>
          <ul
            style={{
              textAlign: 'left',
              color: '#dc3545',
              marginBottom: '30px',
            }}>
            {Object.entries(errores).map(([tipo, mensaje], index) => (
              <li key={tipo} style={{ marginBottom: '10px' }}>
                {tipo}: {mensaje}
              </li>
            ))}
          </ul>
          <div
            style={{
              fontSize: '0.9em',
              color: '#888',
            }}>
            UUID utilizado: {uuidSeleccionado}
          </div>
          <button
            type="button"
            onClick={() => window.location.reload()}
            style={{
              marginTop: '20px',
              padding: '10px 20px',
              backgroundColor: '#007bff',
              color: 'white',
              border: 'none',
              borderRadius: '5px',
              cursor: 'pointer',
            }}>
            Reintentar
          </button>
        </div>
      </div>
    );
  }

  // Mostrar mensaje si no hay UUID
  if (!uuidSeleccionado) {
    return (
      <div
        style={{
          minHeight: '100vh',
          backgroundColor: '#f8f9fa',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
        <div
          style={{
            textAlign: 'center',
            maxWidth: '600px',
            padding: '40px',
            backgroundColor: '#fff',
            borderRadius: '8px',
            boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
          }}>
          <div
            style={{
              fontSize: '1.5em',
              marginBottom: '20px',
              color: '#666',
            }}>
            Selecciona una ficha.
          </div>
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
            }}
          >
            <button
              className="btn btn-primary btn-lg"
              onClick={handleToggleSidebar}
              style={{
                padding: '12px 30px',
                fontSize: '16px',
                borderRadius: '6px'
              }}
            >
              <i className="fas fa-list-alt me-2"></i>
              {' Ver Fichas Disponibles'}
            </button>
          </div>
          {isSidebarVisible && (
            <FichasSidebar
              isVisible={isSidebarVisible}
              onToggle={handleToggleSidebar}
              onFichaSelect={handleFichaSelect}
              selectedFichaId={selectedFichaId}
            />
          )}
        </div>
      </div>
    );
  }

  // Mostrar el contenido principal si la carga está completa
  if (cargaCompleta && datosFormulario) {
    return (
      <div
        style={{
          minHeight: '100vh',
          backgroundColor: '#f8f9fa',
        }}>
        <div
          style={{
            padding: '20px',
            backgroundColor: '#fff',
            borderBottom: '1px solid #dee2e6',
            marginBottom: '20px',
          }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h1
              style={{
                margin: 0,
                color: '#333',
                fontSize: '1.8em', // aumentado
              }}>
              Información de la ficha
            </h1>
            <button
              className="btn btn-outline-primary"
              onClick={handleToggleSidebar}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '8px 16px',
                fontSize: '14px',
                borderRadius: '20px'
              }}
            >
              <i className="fas fa-list-alt"></i>
              Fichas
            </button>
          </div>
          <hr />
          {infoGeneral && (
            <div className="container-fluid" style={{ marginTop: '12px', color: '#333',fontSize:'1.2em' }}>
              <div className="row g-3">
                <div className="col-md-3">
                  <div><strong>Cuenta: </strong><span>{infoGeneral?.nombre_cuenta || '-'}</span></div>
                  <div><strong>Parcela: </strong><span>{infoGeneral?.nombre_parcela || '-'}</span></div>
                </div>
                <div className="col-md-3">
                  <div><strong>Inspector: </strong><span>{infoGeneral?.nombre_inspector || '-'}</span></div>
                  <div><strong>Fecha Inspección: </strong><span>{infoGeneral?.inspeccion_fecha || '-'}</span></div>
                </div>
  
                <div className="col-md-3">
                  <div><strong>Cooperativa: </strong><span>{infoGeneral?.cooperativa || '-'}</span></div>
                  <div><strong>Parcela Principal: </strong><span>{infoGeneral?.tipo_parcela || '-'}</span></div>
                </div>


                <div className="col-md-3">
                  <LinkDownloadDashboard
                    className="btn btn-sirio btn-outline-secondary w-100"
                    url={`pdf/sic_pp/${uuidSeleccionado}`}
                    label={<><i className='fa fa-download'></i> Descargar PDF</>}
                  />
                </div>
              </div>

            </div>
          )}
        </div>

        <div style={{ padding: '0 20px' }}>
          <GeneraMenuGrupos />
        </div>

        {isSidebarVisible && (
          <FichasSidebar
            isVisible={isSidebarVisible}
            onToggle={handleToggleSidebar}
            onFichaSelect={handleFichaSelect}
            selectedFichaId={selectedFichaId}
          />
        )}

        <BotonVolverArriba />
      </div>
    );
  }

  // Estado por defecto mientras se procesa
  return (
    <div
      style={{
        minHeight: '100vh',
        backgroundColor: '#f8f9fa',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}>
      <div style={{ textAlign: 'center' }}>
        <div
          style={{
            fontSize: '1.2em',
            color: '#666',
          }}>
          Procesando datos...
        </div>
      </div>
    </div>
  );
};

/**
 * Componente principal que envuelve el contenido con el proveedor de datos
 */
const FormMasterDirecto = () => {
  return (
    <ProveedorDatosApi>
      <FormMasterDirectoContent />
    </ProveedorDatosApi>
  );
};

export default FormMasterDirecto;